import { socialNetworkLabels, type SocialLink } from '@hue/contracts';
import { Button, Combobox, IconButton, Input } from '@hue/design-system';
import { Plus, Trash2 } from 'lucide-react';

export function SocialLinksEditor({
  value,
  onChange,
  disabled = false,
  error,
}: {
  value: SocialLink[];
  onChange: (value: SocialLink[]) => void;
  disabled?: boolean;
  error?: string | undefined;
}) {
  function change(index: number, update: Partial<SocialLink>) {
    onChange(value.map((item, at) => (at === index ? { ...item, ...update } : item)));
  }
  return (
    <fieldset disabled={disabled} className="grid min-w-0 gap-4">
      <legend className="mb-2 text-sm font-semibold">Redes sociais</legend>
      <p className="text-sm text-content-muted">
        Adicione somente os perfis que utiliza. A exibição no portal é controlada em Configurações ›
        Portal de clientes. Redes em Outros aparecem como nome e @, sem link.
      </p>
      {value.map((link, index) => (
        <div key={index} className="grid min-w-0 gap-3 rounded-xl border border-surface-border p-3">
          <div className="flex min-w-0 items-end gap-2">
            <div className="min-w-0 flex-1">
              <Combobox
                disabled={disabled}
                label={`Rede social ${index + 1}`}
                placeholder="Escolha a rede social"
                selectedLabel={socialNetworkLabels[link.network]}
                options={Object.entries(socialNetworkLabels).map(([value, label]) => ({
                  value,
                  label,
                }))}
                onSelect={(choice) => {
                  if (choice.kind === 'option')
                    change(index, { network: choice.value as SocialLink['network'], name: '' });
                }}
              />
            </div>
            <IconButton
              label={`Remover rede social ${index + 1}`}
              icon={<Trash2 />}
              tone="danger"
              onClick={() => onChange(value.filter((_, at) => at !== index))}
            />
          </div>
          <div className="grid min-w-0 gap-3 sm:grid-cols-2">
            {link.network === 'OTHER' ? (
              <Input
                label={`Nome da rede social ${index + 1}`}
                value={link.name}
                maxLength={60}
                onChange={(event) => change(index, { name: event.target.value })}
              />
            ) : null}
            <Input
              label={`@ da rede social ${index + 1}`}
              value={link.handle}
              maxLength={101}
              placeholder="@seuperfil"
              hint="Informe o @, sem o endereço da página."
              onChange={(event) => change(index, { handle: event.target.value })}
            />
          </div>
        </div>
      ))}
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <div>
        <Button
          type="button"
          variant="outline"
          className="h-auto min-h-11 max-w-full whitespace-normal py-2"
          disabled={disabled || value.length >= 20}
          onClick={() => onChange([...value, { network: 'INSTAGRAM', name: '', handle: '' }])}
        >
          <Plus className="size-4" aria-hidden="true" /> Adicionar rede social
        </Button>
      </div>
    </fieldset>
  );
}
